import { useEffect, useState } from "react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { UsersThree, CheckCircle, WarningCircle, ArrowsClockwise, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export function EmploymentHeroPanel({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const refresh = () => api.get("/eh/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await api.post("/eh/oauth/start");
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start Employment Hero connection");
      setConnecting(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/availability/sync");
      toast.success(`Synced ${r.data?.stats?.employees ?? 0} employees`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Employment Hero? Cached availability data will remain until the next sync.")) return;
    setDisconnecting(true);
    try {
      await api.post("/eh/disconnect");
      toast.success("Disconnected");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  };

  if (!status) return null;

  const notConfigured = !status.configured;
  const connected = status.connected;

  return (
    <div className="hairline rounded-lg p-5 bg-white" data-testid="settings-employment-hero">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#4f4c90]/15 border border-[#4f4c90]/30 flex items-center justify-center">
            <UsersThree size={20} weight="duotone" className="text-[#8B87C8]" />
          </div>
          <div>
            <div className="font-heading font-semibold">Employment Hero HR</div>
            <p className="text-xs text-slate-500 mt-1">
              Sync team leave &amp; availability so the app knows who&apos;s out today.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {notConfigured && (
            <span className="chip status-waiting flex items-center gap-1">
              <WarningCircle size={12} weight="duotone" /> Not configured
            </span>
          )}
          {status.configured && connected && (
            <span className="chip status-in_progress flex items-center gap-1">
              <CheckCircle size={12} weight="duotone" /> Connected
            </span>
          )}
          {status.configured && !connected && (
            <span className="chip status-waiting flex items-center gap-1">
              <WarningCircle size={12} weight="duotone" /> Not connected
            </span>
          )}
        </div>
      </div>

      {notConfigured && (
        <div className="hairline rounded-md p-3 mt-4 bg-amber-50 border-amber-200 text-xs text-amber-900 space-y-1">
          <div className="font-medium">Backend not yet configured.</div>
          <div>
            Add <span className="font-mono">EH_CLIENT_ID</span> and <span className="font-mono">EH_CLIENT_SECRET</span> to the backend <span className="font-mono">.env</span>, then redeploy.
            Register redirect URI:
          </div>
          <div className="font-mono bg-white/70 px-2 py-1 rounded text-[10px] mt-1 break-all">
            https://systems-helpdesk.auroraenergy.co/api/eh/oauth/callback
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Employees" value={status.employee_count ?? 0} />
        <Stat
          label="Last sync"
          value={status.last_sync_at ? timeAgo(status.last_sync_at) : "—"}
          sub={status.last_sync_stats ? `${status.last_sync_stats.on_leave ?? 0} on leave` : null}
        />
        <Stat
          label="Connected"
          value={status.connected_at ? timeAgo(status.connected_at) : "—"}
        />
        <Stat
          label="Scopes"
          value={status.scope ? status.scope.split(" ").length : "—"}
          sub={status.scope || null}
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {isAdmin && !connected && (
          <Button
            onClick={connect}
            disabled={connecting || notConfigured}
            className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1"
            data-testid="eh-connect-btn"
          >
            <LinkIcon size={14} /> {connecting ? "Redirecting…" : "Connect Employment Hero"}
          </Button>
        )}
        {isAdmin && connected && (
          <>
            <Button
              onClick={syncNow}
              disabled={syncing}
              variant="outline"
              className="h-9 gap-1 border-slate-200 bg-white"
              data-testid="eh-sync-btn"
            >
              <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync now"}
            </Button>
            <Button
              onClick={disconnect}
              disabled={disconnecting}
              variant="outline"
              className="h-9 border-red-200 text-red-600 bg-white hover:bg-red-50"
              data-testid="eh-disconnect-btn"
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </Button>
          </>
        )}
      </div>

      {connected && isAdmin && <ManualLinkTable />}
    </div>
  );
}

function ManualLinkTable() {
  const [users, setUsers] = useState([]);
  const [ehEmployees, setEhEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [q, setQ] = useState({});
  const [savingId, setSavingId] = useState("");

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data.filter((u) => u.is_active !== false && (u.role === "admin" || u.role === "agent"))));
  const loadEmployees = () => {
    setLoadingEmps(true);
    api.get("/eh/employees/all")
      .then((r) => setEhEmployees(r.data.items || []))
      .catch(() => toast.error("Couldn't load Employment Hero directory"))
      .finally(() => setLoadingEmps(false));
  };
  useEffect(() => { loadUsers(); loadEmployees(); }, []);

  const empById = new Map(ehEmployees.map((e) => [e.id, e]));

  const save = async (userId, employeeId) => {
    setSavingId(userId);
    try {
      await api.patch("/eh/user-link", { user_id: userId, employee_id: employeeId || null });
      toast.success(employeeId ? "Linked" : "Cleared");
      await loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingId(""); }
  };

  return (
    <div className="mt-5 hairline rounded-md bg-slate-50/60 p-4" data-testid="eh-manual-link-table">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-heading font-semibold text-sm">Team member mapping</div>
          <div className="text-[11px] text-slate-500">
            Manually link app users to Employment Hero employees when auto-match (email/name) doesn&apos;t work.
            {loadingEmps ? " Loading directory…" : ` ${ehEmployees.length} EH people available.`}
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {users.map((u) => {
          const linked = u.eh_employee_id ? empById.get(u.eh_employee_id) : null;
          const search = (q[u.id] || "").toLowerCase();
          const filtered = search
            ? ehEmployees.filter((e) => (e.full_name || "").toLowerCase().includes(search) || (e.email || "").toLowerCase().includes(search)).slice(0, 8)
            : [];
          return (
            <div key={u.id} className="hairline rounded p-2.5 bg-white flex items-start gap-3 flex-wrap" data-testid={`eh-link-row-${u.email}`}>
              <div className="min-w-[180px] flex-shrink-0">
                <div className="text-sm font-medium truncate">{u.name}</div>
                <div className="text-[11px] text-slate-500 font-mono truncate">{u.email}</div>
              </div>
              {linked ? (
                <div className="flex-1 min-w-[240px] flex items-center gap-2">
                  <div className="flex-1 hairline rounded px-2 py-1 bg-emerald-50/50 border-emerald-200 min-w-0">
                    <div className="text-xs text-emerald-900 flex items-center gap-1 truncate">
                      <LinkIcon size={11} weight="duotone" />
                      <span className="font-medium truncate">{linked.full_name}</span>
                      <span className="text-emerald-700 font-mono text-[10px] truncate">· {linked.job_title || ""}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => save(u.id, null)}
                    disabled={savingId === u.id}
                    variant="outline"
                    className="h-7 text-[11px] border-slate-200 bg-white"
                    data-testid={`eh-clear-link-${u.email}`}
                  >Unlink</Button>
                </div>
              ) : (
                <div className="flex-1 min-w-[240px] relative">
                  <input
                    className="w-full text-xs h-8 px-2 rounded border border-slate-200 bg-white"
                    placeholder={u.eh_employee_id === undefined ? "Auto (or type to override…)" : "Type name or email…"}
                    value={q[u.id] || ""}
                    onChange={(e) => setQ({ ...q, [u.id]: e.target.value })}
                    data-testid={`eh-search-${u.email}`}
                  />
                  {filtered.length > 0 && (
                    <div className="absolute top-9 left-0 right-0 z-10 max-h-56 overflow-y-auto bg-white hairline rounded-md shadow-md">
                      {filtered.map((e) => (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => { save(u.id, e.id); setQ({ ...q, [u.id]: "" }); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          data-testid={`eh-pick-${e.id}`}
                        >
                          <div className="text-xs font-medium truncate">{e.full_name}</div>
                          <div className="text-[10px] text-slate-500 font-mono truncate">{e.email} · {e.job_title || ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {users.length === 0 && <div className="text-xs text-slate-500 py-3 text-center">No staff users to map.</div>}
      </div>
      <div className="text-[10px] text-slate-500 mt-3">
        Manual links take priority over email / name auto-match on the next sync. Unlinking falls back to auto-match.
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="hairline rounded-md p-3 bg-slate-50/60">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="font-heading text-lg font-bold text-slate-800 mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  );
}
